import {
  isImageFile,
  isPdfFile,
  matchesPattern,
} from "../../utils/pdfDestinations";

const JOURNEY_LOG_PATTERN = "*journeylog*";
const JOURNEY_LOG_PREFLIGHT_PATTERN = "*journeylogpreflight*";

export const REVIEW_ZOOM_MIN = 0.5;
export const REVIEW_ZOOM_MAX = 8;
export const REVIEW_ZOOM_STEP = 1.25;
export const REVIEW_FIT_SCALE = 1;
export const REVIEW_PAN_NUDGE = 48;

export type JourneyLogReviewFileKind = "image" | "pdf";

export type ReviewViewport = {
  scale: number;
  x: number;
  y: number;
  rotation: number;
};

export const getJourneyLogReviewFileKind = (
  fileName: string,
): JourneyLogReviewFileKind | null => {
  if (isPdfFile(fileName)) return "pdf";
  if (isImageFile(fileName)) return "image";
  return null;
};

export const selectJourneyLogReviewFiles = (fileNames: string[] = []) =>
  fileNames.filter((fileName) => {
    if (!matchesPattern(fileName, JOURNEY_LOG_PATTERN)) return false;
    if (matchesPattern(fileName, JOURNEY_LOG_PREFLIGHT_PATTERN)) return false;
    return getJourneyLogReviewFileKind(fileName) !== null;
  });

export const clampReviewZoom = (scale: number) =>
  Math.min(REVIEW_ZOOM_MAX, Math.max(REVIEW_ZOOM_MIN, scale));

export const zoomInReview = (scale: number) =>
  clampReviewZoom(scale * REVIEW_ZOOM_STEP);

export const zoomOutReview = (scale: number) =>
  clampReviewZoom(scale / REVIEW_ZOOM_STEP);

export const createFitReviewViewport = (): ReviewViewport => ({
  scale: REVIEW_FIT_SCALE,
  x: 0,
  y: 0,
  rotation: 0,
});

export const panReviewViewport = (
  viewport: ReviewViewport,
  dx: number,
  dy: number,
): ReviewViewport => ({
  ...viewport,
  x: viewport.x + dx,
  y: viewport.y + dy,
});

export const rotateReviewViewport = (
  viewport: ReviewViewport,
  delta = 90,
): ReviewViewport => ({
  ...viewport,
  rotation: ((viewport.rotation + delta) % 360 + 360) % 360,
});

export const buildReviewPanZoomTransform = (viewport: ReviewViewport) =>
  `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.scale})`;

export const buildReviewTransform = (viewport: ReviewViewport) =>
  `${buildReviewPanZoomTransform(viewport)} rotate(${viewport.rotation}deg)`;

export const applyReviewKeyPan = (
  viewport: ReviewViewport,
  key: string,
): ReviewViewport | null => {
  if (key === "ArrowLeft") return panReviewViewport(viewport, -REVIEW_PAN_NUDGE, 0);
  if (key === "ArrowRight") return panReviewViewport(viewport, REVIEW_PAN_NUDGE, 0);
  if (key === "ArrowUp") return panReviewViewport(viewport, 0, -REVIEW_PAN_NUDGE);
  if (key === "ArrowDown") return panReviewViewport(viewport, 0, REVIEW_PAN_NUDGE);
  return null;
};
